import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Check, Upload, FileText, Bell, Car, Landmark, Clock, List } from "lucide-react";
import Logo from "@/components/Logo";
import { getTramiteById, uploadDocumento } from "@/lib/api";
import { toast } from "sonner";

const ESTADO_COLOR = {
  Pendiente: "#7a7a7a", Cargado: "var(--af-orange-core)", "En revisión": "#F2B705",
  Aceptado: "#22c55e", Rechazado: "#ef4444", "Requiere actualización": "#F2B705", Vencido: "#ef4444",
};

function Stepper({ etapas, etapaActual }) {
  const idx = etapas.findIndex((e) => e.id === etapaActual);
  return (
    <div className="af-card p-6" data-testid="stepper">
      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-2">
        {etapas.map((e, i) => {
          const done = i < idx, current = i === idx;
          const color = current ? "var(--af-orange-core)" : done ? "#22c55e" : "#2e2e2e";
          return (
            <React.Fragment key={e.id}>
              <div className="flex items-center gap-3 md:flex-col md:text-center md:flex-1" data-testid={`etapa-${e.id}`}>
                <div className="rounded-full flex items-center justify-center shrink-0 font-bold"
                  style={{ width: 40, height: 40, background: current ? "rgba(232,93,4,0.18)" : "transparent",
                           border: `2px solid ${color}`, color }}>
                  {done ? <Check size={20} /> : i + 1}
                </div>
                <div>
                  <div className="text-white font-semibold text-sm" style={{ color: current ? "var(--af-orange-core)" : "#fff" }}>{e.etapa}</div>
                  <div className="text-white/50 text-xs hidden md:block">{e.descripcion}</div>
                </div>
              </div>
              {i < etapas.length - 1 && <div className="hidden md:block h-0.5 flex-1" style={{ background: done ? "#22c55e" : "#2e2e2e" }} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function DocRow({ tramiteId, doc, onUpdated }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      await uploadDocumento(tramiteId, doc.id_documento, file);
      toast.success("Documento cargado.");
      onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos completar la carga en este momento. Inténtalo nuevamente.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  const requeridoLabel = doc.requerido === "SI" ? "Requerido" : doc.requerido === "CONDICIONAL" ? "Condicional" : "Opcional";
  return (
    <div className="af-card p-4 flex items-center justify-between gap-3" data-testid={`doc-${doc.id_documento}`}>
      <div className="flex items-start gap-3 min-w-0">
        <FileText className="text-af-orange-core shrink-0 mt-0.5" size={22} />
        <div className="min-w-0">
          <div className="text-white font-medium text-sm">{doc.documento}
            <span className="text-white/40 text-xs ml-2">· {requeridoLabel}</span>
          </div>
          {doc.observacion ? <div className="text-white/45 text-xs mt-0.5">{doc.observacion}</div> : null}
          {doc.nombre_original ? <div className="text-white/50 text-xs mt-0.5 truncate">{doc.nombre_original}</div> : null}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-xs font-semibold" style={{ color: ESTADO_COLOR[doc.estado] || "#aaa" }}>{doc.estado}</span>
        <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={onFile}
          data-testid={`file-input-${doc.id_documento}`} />
        <button data-testid={`upload-${doc.id_documento}`} onClick={() => inputRef.current?.click()} disabled={busy}
          className="af-btn-ghost" style={{ padding: "0.5rem 0.8rem" }}>
          {busy ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
          {doc.estado === "Pendiente" ? "Subir" : "Reemplazar"}
        </button>
      </div>
    </div>
  );
}

export default function Tramite() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = () => {
    if (!id) { setErr("Selecciona un trámite."); setLoading(false); return Promise.resolve(); }
    return getTramiteById(id).then((tr) => { setT(tr); if (!tr) setErr("Aún no tienes un trámite activo."); })
      .catch((e) => setErr(e?.response?.data?.detail || "No pudimos cargar tu trámite.")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  return (
    <div className="min-h-screen" style={{ background: "var(--af-black)" }}>
      <div className="sticky top-0 z-40" style={{ background: "rgba(13,13,13,0.95)", backdropFilter: "blur(12px)", borderBottom: "1px solid var(--af-border-dark)" }}>
        <div className="af-container flex items-center gap-3" style={{ height: 68 }}>
          <button data-testid="tramite-back" onClick={() => navigate("/mis-tramites")} className="text-white/80 hover:text-af-orange-core"><ArrowLeft size={22} /></button>
          <Link to="/"><Logo height={40} /></Link>
          <div className="flex-1" />
          <Link to="/mis-tramites" className="text-white/70 hover:text-af-orange-core flex items-center gap-1.5 text-sm" data-testid="tramite-mis-tramites"><List size={18} /> Mis trámites</Link>
        </div>
      </div>

      <div className="af-container py-8 md:py-12 max-w-4xl" data-testid="tramite-page">
        {loading && <div className="af-card p-12 text-center"><Loader2 className="animate-spin text-af-orange-core mx-auto" size={36} /></div>}

        {!loading && !t && (
          <div className="af-card p-10 text-center">
            <p className="text-white/80">{err || "Aún no tienes un trámite activo."}</p>
            <button onClick={() => navigate("/")} className="af-btn-primary mt-6">Volver al inicio</button>
          </div>
        )}

        {!loading && t && (
          <>
            <h1 className="h-impact text-white text-3xl md:text-4xl mb-2">Seguimiento de tu trámite</h1>
            <div className="flex flex-wrap gap-4 text-sm text-white/70 mb-6">
              <span className="flex items-center gap-1.5"><Car size={16} className="text-af-orange-core" /> {t.vehiculo || "Vehículo por definir"}</span>
              <span className="flex items-center gap-1.5"><Clock size={16} className="text-af-orange-core" /> {t.modalidad}</span>
              {t.institucion ? <span className="flex items-center gap-1.5"><Landmark size={16} className="text-af-orange-core" /> {t.institucion} · {t.producto_nombre}</span> : null}
            </div>

            <Stepper etapas={t.etapas} etapaActual={t.etapa_actual} />

            {t.notificaciones?.length > 0 && (
              <div className="af-card p-5 mt-6" data-testid="notificaciones">
                <div className="flex items-center gap-2 text-white font-semibold mb-3"><Bell size={18} className="text-af-orange-core" /> Notificaciones</div>
                <div className="space-y-2">
                  {t.notificaciones.map((n, i) => (
                    <div key={i} className="text-white/70 text-sm rounded-lg p-3" style={{ background: "#111" }}>{n.mensaje}</div>
                  ))}
                </div>
              </div>
            )}

            {t.requiere_revision_manual && (
              <div className="af-card p-5 mt-6" style={{ borderColor: "rgba(242,183,5,0.5)" }}>
                <p className="text-white/80 text-sm">Estamos revisando tu caso. Un asesor te contactará para continuar con tu trámite.</p>
              </div>
            )}

            {t.documentos?.length > 0 && (
              <div className="mt-8">
                <h2 className="h-impact text-white text-2xl mb-4">Documentos</h2>
                <div className="space-y-3">
                  {t.documentos.map((d) => <DocRow key={d.id_documento} tramiteId={t.tramite_id} doc={d} onUpdated={load} />)}
                </div>
                <p className="text-white/45 text-xs mt-4">Formatos aceptados: PDF, JPG o PNG (máx. 10 MB). Tus documentos son privados y solo tú puedes verlos.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
